/**
 * System Monitoring Dashboard
 * 
 * Provides real-time visibility into system health, performance metrics,
 * and active alerts for administrators.
 */

import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  XCircle,
  Clock,
  Database,
  Server,
  Workflow,
  TrendingUp,
  RefreshCw,
  AlertCircle,
  Loader2,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import * as monitoringApi from '../../api/monitoring';

const MonitoringDashboard = () => {
  const { data: metricsData, isLoading, refetch, isFetching } = useQuery({
    queryKey: ['monitoring-metrics'],
    queryFn: monitoringApi.getMonitoringMetrics,
    refetchInterval: 30000, // Refresh every 30 seconds
  });

  const metrics = metricsData?.data || {};
  const health = metrics.system_health || {};
  const workflowMetrics = metrics.workflow_metrics || {};
  const apiMetrics = metrics.api_metrics || {};
  const alerts = metrics.alerts || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="monitoring-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Monitoring</h1>
          <p className="text-gray-500 mt-1">Real-time system health and performance metrics</p>
        </div>
        <Button onClick={() => refetch()} disabled={isFetching} variant="outline">
          <RefreshCw className={`w-4 h-4 mr-2 ${isFetching ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Active Alerts */}
      {alerts.length > 0 && (
        <Card className="border-red-200 bg-red-50/50">
          <CardHeader className="pb-2">
            <CardTitle className="text-lg flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              Active Alerts
              <Badge variant="destructive">{alerts.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {alerts.map((alert, index) => (
                <div 
                  key={index}
                  className={`flex items-start gap-3 p-3 rounded-lg ${
                    alert.severity === 'critical' ? 'bg-red-100' : 'bg-yellow-100'
                  }`}
                >
                  <AlertCircle className={`w-5 h-5 mt-0.5 ${
                    alert.severity === 'critical' ? 'text-red-600' : 'text-yellow-600'
                  }`} />
                  <div className="flex-1">
                    <p className="font-medium text-gray-900">{alert.title}</p>
                    <p className="text-sm text-gray-600">{alert.message}</p>
                  </div>
                  <Badge variant={alert.severity === 'critical' ? 'destructive' : 'warning'}>
                    {alert.severity}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* System Health */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <HealthCard
          title="System Status"
          status={health.status}
          icon={Server}
        />
        <HealthCard
          title="Database"
          status={health.checks?.database?.status}
          message={health.checks?.database?.message}
          icon={Database}
        />
        <HealthCard
          title="Error Rate"
          status={health.checks?.error_rate?.status}
          message={health.checks?.error_rate?.message}
          icon={Activity}
        />
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Active Workflows"
          value={workflowMetrics.active_workflows || 0}
          icon={Workflow}
          color="blue"
        />
        <MetricCard
          title="Stale Workflows"
          value={workflowMetrics.stale_workflows || 0}
          icon={Clock}
          color={workflowMetrics.stale_workflows > 0 ? 'red' : 'green'}
          alert={workflowMetrics.stale_workflows > 5}
        />
        <MetricCard
          title="Pending Tasks"
          value={workflowMetrics.pending_tasks || 0}
          icon={AlertTriangle}
          color={workflowMetrics.pending_tasks > 20 ? 'yellow' : 'green'}
        />
        <MetricCard
          title="Completion Rate"
          value={`${workflowMetrics.completion_rate_percent || 0}%`}
          icon={TrendingUp}
          color="green"
        />
      </div>

      {/* API Errors */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <Activity className="w-5 h-5" />
            API Error Summary (Last 24h)
          </CardTitle>
          <CardDescription>
            Total errors: {apiMetrics.total_errors || 0}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {apiMetrics.by_endpoint?.length > 0 ? (
            <div className="space-y-2">
              {apiMetrics.by_endpoint.slice(0, 10).map((endpoint, index) => (
                <div key={index} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                  <code className="text-sm text-gray-700 truncate max-w-md">{endpoint.endpoint}</code>
                  <div className="flex items-center gap-4">
                    {endpoint.errors > 0 && (
                      <Badge variant="destructive">{endpoint.errors} errors</Badge>
                    )}
                    {endpoint.warnings > 0 && (
                      <Badge variant="warning">{endpoint.warnings} warnings</Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">No errors in the last 24 hours</p>
          )}
        </CardContent>
      </Card>

      {/* Last Updated */}
      <p className="text-sm text-gray-400 text-center">
        Last updated: {metrics.generated_at ? new Date(metrics.generated_at).toLocaleString() : 'N/A'}
      </p>
    </div>
  );
};

const HealthCard = ({ title, status, message, icon: Icon }) => {
  const statusConfig = {
    healthy: { color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle2 },
    degraded: { color: 'text-yellow-600', bg: 'bg-yellow-50', icon: AlertTriangle },
    unhealthy: { color: 'text-red-600', bg: 'bg-red-50', icon: XCircle },
    unknown: { color: 'text-gray-600', bg: 'bg-gray-50', icon: AlertCircle },
  };

  const config = statusConfig[status] || statusConfig.unknown;
  const StatusIcon = config.icon;

  return (
    <Card className={config.bg}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Icon className="w-5 h-5 text-gray-600" />
            <div>
              <p className="font-medium text-gray-900">{title}</p>
              {message && <p className="text-sm text-gray-500">{message}</p>}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusIcon className={`w-5 h-5 ${config.color}`} />
            <span className={`text-sm font-medium ${config.color} capitalize`}>
              {status || 'Unknown'}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const MetricCard = ({ title, value, icon: Icon, color, alert }) => {
  const colorClasses = {
    blue: 'text-blue-600 bg-blue-50',
    green: 'text-green-600 bg-green-50',
    yellow: 'text-yellow-600 bg-yellow-50',
    red: 'text-red-600 bg-red-50',
  };

  return (
    <Card className={alert ? 'border-red-300 ring-1 ring-red-200' : ''}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div className={`p-2 rounded-lg ${colorClasses[color]?.split(' ')[1] || 'bg-gray-50'}`}>
            <Icon className={`w-5 h-5 ${colorClasses[color]?.split(' ')[0] || 'text-gray-600'}`} />
          </div>
          {alert && <AlertTriangle className="w-4 h-4 text-red-500" />}
        </div>
        <p className="text-2xl font-bold text-gray-900 mt-3">{value}</p>
        <p className="text-sm text-gray-500">{title}</p>
      </CardContent>
    </Card>
  );
};

export default MonitoringDashboard;
